import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Badge } from "./components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./components/ui/dialog";
import { Input } from "./components/ui/input";
import { Calendar as CalendarIcon, ChevronRight, ChevronLeft, RefreshCw, Users, Check, Loader2, Printer, CheckCircle, X, Trash2 } from "lucide-react";
import { toast } from "sonner";

const WeeklyScheduleComponent = ({ API, token, deacons, language, getAuthHeaders, refreshKey, serviceTypes }) => {
  const [allSchedules, setAllSchedules] = useState({});
  const [activeService, setActiveService] = useState("");
  const [currentWeekIndex, setCurrentWeekIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [selectedDeaconIds, setSelectedDeaconIds] = useState([]);
  const [editPassword, setEditPassword] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [registering, setRegistering] = useState(false);
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
  const [registerDate, setRegisterDate] = useState("");
  const [registerSchedule, setRegisterSchedule] = useState(null);
  const [registerReadingType, setRegisterReadingType] = useState("");
  const [deaconReadingTypes, setDeaconReadingTypes] = useState({});
  const [rankConfig, setRankConfig] = useState(null);
  const [rankConfigDialogOpen, setRankConfigDialogOpen] = useState(false);
  const [editableQuotas, setEditableQuotas] = useState([]);
  const [ranks, setRanks] = useState([]);

  // Sub-types for each schedule group
  const groupSubTypes = {
    'القراءات': [
      'انجيل باكر', 'البولس', 'البولس الألماني', 'الكاثوليكون', 'الكاثوليكون الألماني',
      'الإبركسيس', 'الإبركسيس الألماني', 'السنكسار', 'انجيل القداس', 'انجيل القداس الألماني', 'بدء التوزيع'
    ],
    'خدمة خورس': [
      'قياده خورس الجزء الأول', 'قياده خورس الجزء الثاني', 'قياده خورس الجزء الثالث', 'حضور حصة الألحان'
    ],
    'خدمة المذبح': ['خدمة المذبح']
  };

  // Deacons per week per group
  const groupDeaconsCount = { 'القراءات': 8, 'خدمة خورس': 8, 'خدمة المذبح': 8 };

  const isRTL = language === 'ar';

  const t = useCallback((key) => {
    const tr = {
      ar: {
        weeklyScheduleTitle: 'الجدول الأسبوعي للخدمة',
        autoRotation: 'تلقائي بالتناوب',
        currentWeek: 'الأسبوع الحالي',
        nextWeek: 'التالي', prevWeek: 'السابق',
        edit: 'تعديل', save: 'حفظ', cancel: 'إلغاء',
        deaconsServing: 'الشمامسة في الخدمة',
        noSchedule: 'لا يوجد جدول',
        generating: 'جاري التجهيز...',
        password: 'كلمة المرور للتأكيد',
        searchDeacons: 'بحث في الشمامسة...',
        selected: 'تم اختيار',
        noDeacons: 'لا يوجد شمامسة مفعّلين',
        refreshSchedules: 'تحديث',
        weekOf: 'أسبوع',
        printAll: 'طباعة الكل',
        registerService: 'تسجيل الخدمة',
        registering: 'جاري التسجيل...',
        chooseDate: 'حدد تاريخ التسجيل',
        registerAndUpdate: 'تسجيل وتحديث',
        overview: 'نظرة عامة',
        printTitle: 'جدول الخدمة',
        allServices: 'جميع الخدمات',
      },
      de: {
        weeklyScheduleTitle: 'Wöchentlicher Dienstplan',
        autoRotation: 'Automatische Rotation',
        currentWeek: 'Aktuelle Woche',
        nextWeek: 'Weiter', prevWeek: 'Zurück',
        edit: 'Bearbeiten', save: 'Speichern', cancel: 'Abbrechen',
        deaconsServing: 'Diensthabende Diakone',
        noSchedule: 'Kein Plan',
        generating: 'Wird erstellt...',
        password: 'Passwort zur Bestätigung',
        searchDeacons: 'Diakone suchen...',
        selected: 'Ausgewählt',
        noDeacons: 'Keine aktiven Diakone',
        refreshSchedules: 'Aktualisieren',
        weekOf: 'Woche vom',
        printAll: 'Alle drucken',
        registerService: 'Dienst registrieren',
        registering: 'Wird registriert...',
        chooseDate: 'Datum auswählen',
        registerAndUpdate: 'Registrieren & aktualisieren',
        overview: 'Übersicht',
        printTitle: 'Dienstplan',
        allServices: 'Alle Dienste',
      }
    };
    return tr[language]?.[key] || key;
  }, [language]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString(language === 'ar' ? 'ar-EG' : 'de-DE', { day: 'numeric', month: 'short' });
  };

  const formatFullDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString(language === 'ar' ? 'ar-EG' : 'de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const isCurrentWeek = (schedule) => {
    const today = new Date();
    const start = new Date(schedule.week_start + 'T00:00:00');
    const end = new Date(schedule.week_end + 'T23:59:59');
    return today >= start && today <= end;
  };

  const isPastWeek = (schedule) => {
    const today = new Date(); today.setHours(0,0,0,0);
    return new Date(schedule.week_end + 'T23:59:59') < today;
  };

  // 3 fixed schedule groups
  const scheduleGroups = [
    { id: 'readings', name: language === 'ar' ? 'جدول القراءات' : 'Lesungen', serviceType: 'القراءات' },
    { id: 'chorus', name: language === 'ar' ? 'جدول خدمة خورس' : 'Chor-Dienst', serviceType: 'خدمة خورس' },
    { id: 'altar', name: language === 'ar' ? 'جدول خدمة المذبح' : 'Altardienst', serviceType: 'خدمة المذبح' },
  ];

  // Set default active service on mount
  useEffect(() => {
    if (!activeService) {
      setActiveService(scheduleGroups[0].serviceType);
    }
  }, [activeService]); // eslint-disable-line

  // Ensure schedules for active service type
  // Fetch existing schedules without auto-generating/filling
  const fetchSchedules = useCallback(async (svcType) => {
    const serviceToUse = svcType || activeService;
    if (!serviceToUse) return;
    try {
      const response = await axios.get(`${API}/weekly-schedules`, getAuthHeaders());
      const all = response.data.schedules || [];
      const filtered = all.filter(s => s.service_type === serviceToUse)
        .sort((a, b) => a.week_start.localeCompare(b.week_start));
      setAllSchedules(prev => ({ ...prev, [serviceToUse]: filtered }));
    } catch (error) {
      console.error("Error fetching schedules:", error);
    }
  }, [API, getAuthHeaders, activeService]);

  const ensureSchedules = useCallback(async (svcType, refresh = false) => {
    const serviceToUse = svcType || activeService;
    if (!serviceToUse) return;
    setLoading(true);
    try {
      const dpw = groupDeaconsCount[serviceToUse] || 4;
      const response = await axios.post(
        `${API}/weekly-schedules/auto-ensure?deacons_per_week=${dpw}&service_type=${encodeURIComponent(serviceToUse)}&refresh=${refresh}`,
        {}, getAuthHeaders()
      );
      const data = response.data;
      const sorted = (data.schedules || []).sort((a, b) => a.week_start.localeCompare(b.week_start));
      setAllSchedules(prev => ({ ...prev, [serviceToUse]: sorted }));

      const today = new Date();
      const idx = sorted.findIndex(s => {
        const start = new Date(s.week_start + 'T00:00:00');
        const end = new Date(s.week_end + 'T23:59:59');
        return today >= start && today <= end;
      });
      if (idx >= 0) setCurrentWeekIndex(idx);

      if (data.created_count > 0) {
        toast.success(language === 'ar'
          ? `تم تجهيز ${data.created_count} جدول - ${serviceToUse}`
          : `${data.created_count} Pläne erstellt - ${serviceToUse}`);
      }
    } catch (error) {
      console.error("Error ensuring schedules:", error);
    } finally {
      setLoading(false);
    }
  }, [API, getAuthHeaders, activeService, language]);

  useEffect(() => {
    if (token && activeService) {
      ensureSchedules(activeService);
      // Fetch rank config and ranks for the active service
      axios.get(`${API}/service-rank-config/${encodeURIComponent(activeService)}`, getAuthHeaders())
        .then(res => setRankConfig(res.data))
        .catch(() => {});
      axios.get(`${API}/ranks`, getAuthHeaders())
        .then(res => setRanks(res.data))
        .catch(() => {});
    }
  }, [token, activeService, refreshKey]); // eslint-disable-line

  const handleSaveRankConfig = async () => {
    try {
      const res = await axios.put(
        `${API}/service-rank-config/${encodeURIComponent(activeService)}`,
        { enabled: true, rank_quotas: editableQuotas },
        getAuthHeaders()
      );
      setRankConfig(res.data);
      setRankConfigDialogOpen(false);
      toast.success('تم حفظ إعدادات التوزيع');
    } catch (error) {
      toast.error('خطأ في حفظ الإعدادات');
    }
  };

  const schedules = allSchedules[activeService] || [];
  const currentSchedule = schedules[currentWeekIndex];

  const handleRegisterOpen = (schedule) => {
    setRegisterSchedule(schedule);
    setRegisterDate(schedule.week_start);
    const subTypes = groupSubTypes[activeService] || [activeService];
    setRegisterReadingType(subTypes[0]);
    // Initialize per-deacon reading types for services with multiple sub-types
    if (activeService === 'خدمة خورس' && schedule.deacons) {
      const initial = {};
      schedule.deacons.forEach((id, idx) => {
        initial[id] = subTypes[idx % subTypes.length] || subTypes[0];
      });
      setDeaconReadingTypes(initial);
    }
    setRegisterDialogOpen(true);
  };

  const handleRegisterService = async () => {
    if (!registerSchedule?.deacons?.length || !registerDate) return;
    setRegistering(true);
    try {
      let totalCreated = 0;
      let totalSkipped = 0;
      const subTypes = groupSubTypes[activeService] || [activeService];

      if (activeService === 'خدمة خورس') {
        // Register all as "خدمة خورس"
        const response = await axios.post(`${API}/assignments/bulk`, {
          date: registerDate,
          reading_type: 'خدمة خورس',
          deacon_ids: registerSchedule.deacons
        }, getAuthHeaders());
        totalCreated = response.data.created_count || 0;
        totalSkipped = response.data.skipped_count || 0;
      } else {
        // Single type registration
        const readingType = subTypes[0];
        const response = await axios.post(`${API}/assignments/bulk`, {
          date: registerDate,
          reading_type: readingType,
          deacon_ids: registerSchedule.deacons
        }, getAuthHeaders());
        totalCreated = response.data.created_count || 0;
        totalSkipped = response.data.skipped_count || 0;
      }

      setRegisterDialogOpen(false);
      if (totalCreated > 0) {
        toast.success(language === 'ar'
          ? `تم تسجيل ${totalCreated} شماس - يتم تحديث الجدول...`
          : `${totalCreated} Diakone registriert`);
      }
      if (totalSkipped > 0) {
        toast.info(language === 'ar' ? `${totalSkipped} مسجل مسبقاً` : `${totalSkipped} bereits registriert`);
      }
      // Replace only registered deacons in future schedules
      if (registerSchedule?.deacons?.length > 0) {
        try {
          await axios.post(`${API}/weekly-schedules/replace-registered`, {
            deacon_ids: registerSchedule.deacons
          }, getAuthHeaders());
        } catch (e) { console.error("Replace error:", e); }
      }
      // Refresh all schedules display
      for (const grp of scheduleGroups) {
        await fetchSchedules(grp.serviceType);
      }
    } catch (error) {
      toast.error(language === 'ar' ? 'خطأ في التسجيل' : 'Registrierungsfehler');
    } finally { setRegistering(false); }
  };

  const handleRemoveDeacon = async (scheduleId, deaconId) => {
    try {
      const res = await axios.put(`${API}/weekly-schedules/${scheduleId}/remove-deacon/${deaconId}`, {}, getAuthHeaders());
      // Update local state
      setAllSchedules(prev => {
        const updated = { ...prev };
        if (updated[activeService]) {
          updated[activeService] = updated[activeService].map(s =>
            s.id === scheduleId ? { ...s, deacons: res.data.deacons, deacon_names: res.data.deacon_names } : s
          );
        }
        return updated;
      });
      toast.success(language === 'ar' ? 'تم حذف الشماس من الجدول' : 'Diakon entfernt');
    } catch (error) {
      toast.error(language === 'ar' ? 'خطأ في الحذف' : 'Fehler');
    }
  };

  const handlePrintAll = () => {
    // Get 8 weeks starting from current week index
    const allWeeks = schedules.slice(currentWeekIndex, currentWeekIndex + 8);
    if (allWeeks.length === 0) return;

    const firstHalf = allWeeks.slice(0, 4);
    const secondHalf = allWeeks.slice(4, 8);

    const maxDeacons = Math.max(...allWeeks.map(s => (s.deacon_names || []).length), 0);
    const printWindow = window.open('', '_blank');

    const baseFontSize = maxDeacons <= 5 ? 16 : maxDeacons <= 8 ? 13 : maxDeacons <= 10 ? 11 : 10;
    const headerFontSize = baseFontSize + 2;
    const dateFontSize = baseFontSize;
    const titleFontSize = Math.min(22, baseFontSize + 8);
    const cellPadding = maxDeacons <= 8 ? '5px 5px' : '3px 4px';

    const serviceName = scheduleGroups.find(g => g.serviceType === activeService)?.name || activeService;

    const buildTable = (weeks) => {
      const headers = weeks.map(s => {
        const d = new Date(s.week_start + 'T00:00:00');
        const dayName = d.toLocaleDateString('de-DE', { weekday: 'long' });
        const dateStr = d.toLocaleDateString('de-DE', { day: 'numeric', month: 'short' });
        return `<th colspan="2">${dayName}<br/><span class="date-sub">${dateStr}</span></th>`;
      }).join('');

      const pageMax = Math.max(...weeks.map(s => (s.deacon_names || []).length), 0);
      let rows = '';
      for (let i = 0; i < pageMax; i++) {
        const cells = weeks.map(s => {
          const name = (s.deacon_names || [])[i] || '';
          return `<td class="name-cell">${name}</td><td class="empty-cell"></td>`;
        }).join('');
        rows += `<tr><td class="num">${i + 1}</td>${cells}</tr>`;
      }

      return `<table><thead><tr><th class="num">#</th>${headers}</tr></thead><tbody>${rows}</tbody></table>`;
    };

    printWindow.document.write(`<!DOCTYPE html><html dir="ltr" lang="de"><head><meta charset="UTF-8"><title>${serviceName}</title>
<style>
@page { size: A4 landscape; margin: 0.5cm; }
* { margin: 0; padding: 0; box-sizing: border-box; }
body { font-family: 'Segoe UI', Tahoma, Arial, sans-serif; direction: ltr; color: #333; line-height: 1.2; }
.header { text-align: center; padding: 4px 0; border-bottom: 2px solid #d97706; margin-bottom: 4px; }
.header h1 { font-size: ${titleFontSize}px; color: #92400e; margin-bottom: 1px; }
.header p { font-size: ${dateFontSize}px; color: #78716c; }
.separator { height: 6px; }
table { width: 100%; border-collapse: collapse; margin-top: 2px; }
th { background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%); color: #92400e; padding: ${cellPadding}; border: 2px solid #f59e0b; font-size: ${headerFontSize}px; font-weight: 700; text-align: center; }
th .date-sub { font-size: ${dateFontSize}px; font-weight: 500; color: #b45309; }
td { padding: ${cellPadding}; border: 1px solid #d6d3d1; font-size: ${baseFontSize}px; font-weight: 600; }
.name-cell { text-align: left; }
.empty-cell { width: 50px; }
tr:nth-child(even) { background: #fffbeb; }
.num { width: 22px; font-weight: 700; color: #92400e; background: #fef9c3; text-align: center; font-size: ${baseFontSize}px; }
@media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style></head><body>
<div class="header">
  <h1>${serviceName}</h1>
  <p>${new Date().toLocaleDateString('de-DE', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
</div>
${buildTable(firstHalf)}
${secondHalf.length > 0 ? `<div class="separator"></div>${buildTable(secondHalf)}` : ''}
<script>window.onload=function(){window.print();}</script>
</body></html>`);
    printWindow.document.close();
  };

  const openEditDialog = (schedule) => {
    setEditingSchedule(schedule);
    setSelectedDeaconIds([...(schedule.deacons || [])]);
    setEditPassword(""); setSearchTerm("");
    setEditDialogOpen(true);
  };

  const toggleDeacon = (id) => {
    setSelectedDeaconIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSaveEdit = async () => {
    if (selectedDeaconIds.length === 0) { toast.error(language === 'ar' ? 'اختر شماساً واحداً على الأقل' : 'Mindestens einen Diakon'); return; }
    try {
      await axios.put(`${API}/weekly-schedules/${editingSchedule.id}`, {
        deacon_ids: selectedDeaconIds, notes: editingSchedule.notes || "", password: ""
      }, getAuthHeaders());
      toast.success(language === 'ar' ? 'تم تحديث الجدول' : 'Plan aktualisiert');
      setEditDialogOpen(false);
      fetchSchedules(activeService);
    } catch (error) {
      toast.error(error.response?.status === 401 || error.response?.status === 403
        ? (language === 'ar' ? 'كلمة المرور خاطئة' : 'Falsches Passwort')
        : (language === 'ar' ? 'خطأ في التحديث' : 'Fehler'));
    }
  };

  const activeDeacons = deacons.filter(d => d.schedule_active !== false);
  const sortedActiveDeacons = [...activeDeacons].sort((a, b) => (a.total_participation || 0) - (b.total_participation || 0));
  const filteredDeacons = sortedActiveDeacons.filter(d => d.name?.toLowerCase().includes(searchTerm.toLowerCase()));

  if (loading && schedules.length === 0) {
    return (
      <Card className="shadow-lg border-amber-200" data-testid="weekly-schedule-loading">
        <CardContent className="p-12 flex flex-col items-center justify-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
          <p className="text-amber-700 font-medium">{t('generating')}</p>
        </CardContent>
      </Card>
    );
  }

  if (!activeDeacons.length) {
    return (
      <Card className="shadow-lg border-amber-200" data-testid="weekly-schedule-no-deacons">
        <CardContent className="p-12 text-center">
          <Users className="w-12 h-12 mx-auto text-amber-400 mb-4" />
          <p className="text-amber-700 font-medium">{t('noDeacons')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="weekly-schedule-container">
      <Card className="shadow-lg border-amber-200">
        <CardHeader className="bg-gradient-to-r from-amber-100 to-amber-50 pb-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="flex items-center gap-2 text-amber-900 text-lg">
              <CalendarIcon className="w-5 h-5" />
              {t('weeklyScheduleTitle')}
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="border-green-400 text-green-700 bg-green-50">{t('autoRotation')}</Badge>
              <Button variant="outline" size="sm" onClick={handlePrintAll}
                className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="print-all-btn">
                <Printer className={`w-4 h-4 ${isRTL?'ml-1':'mr-1'}`} />{t('printAll')}
              </Button>
              <Button variant="outline" size="sm" onClick={() => ensureSchedules(activeService)}
                disabled={loading} className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="refresh-schedules-btn">
                <RefreshCw className={`w-4 h-4 ${isRTL?'ml-1':'mr-1'} ${loading?'animate-spin':''}`} />{t('refreshSchedules')}
              </Button>
              <Button variant="outline" size="sm" data-testid="clear-schedules-btn"
                onClick={async () => {
                  if (!window.confirm('هل تريد مسح جميع جداول ' + activeService + '؟')) return;
                  try {
                    const res = await axios.delete(`${API}/weekly-schedules-clear/${encodeURIComponent(activeService)}`, getAuthHeaders());
                    toast.success(`تم مسح ${res.data.deleted_count} جدول`);
                    setAllSchedules(prev => ({ ...prev, [activeService]: [] }));
                  } catch (e) { toast.error('خطأ في مسح الجداول'); }
                }}
                className="border-red-300 text-red-600 hover:bg-red-50">
                <Trash2 className={`w-4 h-4 ${isRTL?'ml-1':'mr-1'}`} />مسح الكل
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-4">
          {/* Schedule Group Tabs */}
          <div className="mb-4">
            <div className="flex gap-2">
              {scheduleGroups.map((grp) => (
                <button
                  key={grp.id}
                  onClick={() => { setActiveService(grp.serviceType); setCurrentWeekIndex(0); }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    activeService === grp.serviceType
                      ? 'bg-amber-500 text-white shadow-sm'
                      : 'bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100'
                  }`}
                  data-testid={`schedule-group-${grp.id}`}
                >
                  {grp.name}
                </button>
              ))}
            </div>
          </div>

          {/* Rank Distribution Config Button */}
          {activeService && (
            <div className="mb-3 flex items-center gap-3 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditableQuotas(rankConfig?.rank_quotas?.length ? [...rankConfig.rank_quotas] : []);
                  setRankConfigDialogOpen(true);
                }}
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                data-testid="rank-config-btn"
              >
                <Users className="w-4 h-4 ml-1" /> إعدادات التوزيع حسب الرتب
              </Button>
              {rankConfig?.enabled && rankConfig?.rank_quotas?.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {rankConfig.rank_quotas.map((q, i) => {
                    const totalInRank = (deacons || []).filter(d => d.rank === q.rank).length;
                    return (
                      <Badge key={i} variant="outline" className="border-amber-300 bg-amber-50 text-amber-800 text-xs">
                        {q.rank}: {q.count} <span className="text-amber-500 mx-1">/</span> <span className="text-gray-500">{totalInRank}</span>
                      </Badge>
                    );
                  })}
                  <Badge variant="outline" className="border-gray-300 bg-gray-50 text-gray-700 text-xs">
                    الإجمالي: {rankConfig.rank_quotas.reduce((s, q) => s + q.count, 0)} <span className="text-amber-500 mx-1">/</span> <span className="text-gray-500">{(deacons || []).length}</span>
                  </Badge>
                </div>
              )}
            </div>
          )}

          {schedules.length > 0 ? (
            <div className="space-y-4">
              {/* Navigation */}
              <div className="flex items-center justify-between bg-amber-50 rounded-lg p-3 border border-amber-200">
                <Button variant="ghost" size="sm"
                  onClick={() => setCurrentWeekIndex(Math.max(0, currentWeekIndex - 1))}
                  disabled={currentWeekIndex === 0} className="text-amber-700" data-testid="prev-week-btn">
                  {isRTL ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
                  {t('prevWeek')}
                </Button>
                <div className="text-center">
                  {currentSchedule && isCurrentWeek(currentSchedule) && (
                    <Badge className="bg-amber-500 text-white mb-1 text-xs">{t('currentWeek')}</Badge>
                  )}
                  <div className="font-bold text-amber-900 text-base flex items-center gap-2 justify-center" data-testid="current-week-label">
                    <span>{formatDate(currentSchedule?.week_start)}</span>
                    <span className="text-amber-500">-</span>
                    <span>{formatDate(currentSchedule?.week_end)}</span>
                  </div>
                  <div className="text-xs text-amber-600 mt-0.5">{currentWeekIndex + 1} / {schedules.length}</div>
                </div>
                <Button variant="ghost" size="sm"
                  onClick={() => setCurrentWeekIndex(Math.min(schedules.length - 1, currentWeekIndex + 1))}
                  disabled={currentWeekIndex >= schedules.length - 1} className="text-amber-700" data-testid="next-week-btn">
                  {t('nextWeek')}
                  {isRTL ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                </Button>
              </div>

              {/* Week Dots */}
              <div className="flex justify-center gap-1.5 py-1">
                {schedules.map((s, i) => (
                  <button key={i} onClick={() => setCurrentWeekIndex(i)}
                    className={`w-2.5 h-2.5 rounded-full transition-all ${
                      i === currentWeekIndex ? 'bg-amber-500 scale-125'
                        : isCurrentWeek(s) ? 'bg-green-400'
                        : isPastWeek(s) ? 'bg-gray-300'
                        : 'bg-amber-200 hover:bg-amber-300'
                    }`} data-testid={`week-dot-${i}`} />
                ))}
              </div>

              {/* Schedule Card */}
              {currentSchedule ? (
                <div className="border border-amber-200 rounded-xl overflow-hidden" data-testid="schedule-card">
                  <div className="bg-amber-50 px-4 py-3 flex items-center justify-between border-b border-amber-200 flex-wrap gap-2">
                    <div>
                      <h3 className="font-bold text-amber-900">{t('weekOf')} {formatFullDate(currentSchedule.week_start)}</h3>
                      <p className="text-xs text-amber-600">{scheduleGroups.find(g => g.serviceType === activeService)?.name || activeService}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleRegisterOpen(currentSchedule)}
                        disabled={registering} className="border-green-300 text-green-700 hover:bg-green-50" data-testid="register-service-btn">
                        <CheckCircle className={`w-4 h-4 ${isRTL?'ml-1':'mr-1'}`} />{t('registerService')}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => openEditDialog(currentSchedule)}
                        className="border-amber-300 text-amber-700 hover:bg-amber-100" data-testid="edit-schedule-btn">
                        {t('edit')}
                      </Button>
                    </div>
                  </div>
                  <div className="p-4">
                    <h4 className="text-sm font-medium text-amber-700 mb-3 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      {t('deaconsServing')} ({currentSchedule.deacon_names?.length || 0})
                    </h4>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {(currentSchedule.deacon_names || []).map((name, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-white border border-amber-100 rounded-lg p-2.5 shadow-sm" data-testid={`deacon-item-${idx}`}>
                          <button
                            onClick={() => handleRemoveDeacon(currentSchedule.id, currentSchedule.deacons[idx])}
                            className="w-5 h-5 rounded-full bg-red-50 hover:bg-red-100 flex items-center justify-center text-red-400 hover:text-red-600 transition-colors flex-shrink-0"
                            data-testid={`remove-deacon-${idx}`}
                            title={language === 'ar' ? 'حذف من الجدول' : 'Aus Plan entfernen'}
                          >
                            <X className="w-3 h-3" />
                          </button>
                          <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold text-xs flex-shrink-0">{idx + 1}</div>
                          <span className="font-medium text-gray-800 text-sm flex-shrink-0">{name}</span>
                          {activeService === 'القراءات' && currentSchedule.deacons?.[idx] && (
                            <select
                              value={deaconReadingTypes[currentSchedule.deacons[idx]] || groupSubTypes['القراءات'][idx % groupSubTypes['القراءات'].length]}
                              onChange={(e) => setDeaconReadingTypes(prev => ({...prev, [currentSchedule.deacons[idx]]: e.target.value}))}
                              className="flex-1 text-xs border border-amber-200 rounded px-1.5 py-1 bg-amber-50 text-amber-800 min-w-0"
                              data-testid={`deacon-reading-type-${idx}`}
                            >
                              {groupSubTypes['القراءات'].map(st => (
                                <option key={st} value={st}>{st}</option>
                              ))}
                            </select>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-amber-500">{t('noSchedule')}</div>
              )}

              {/* Overview */}
              <div className="mt-4">
                <h4 className="text-sm font-medium text-amber-700 mb-2">{t('overview')}</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {schedules.map((s, i) => (
                    <button key={i} onClick={() => setCurrentWeekIndex(i)}
                      className={`w-full text-${isRTL?'right':'left'} p-3 rounded-lg border transition-all ${
                        i === currentWeekIndex ? 'border-amber-400 bg-amber-50 shadow-sm'
                          : isCurrentWeek(s) ? 'border-green-200 bg-green-50 hover:bg-green-100'
                          : 'border-gray-100 hover:bg-gray-50'
                      }`} data-testid={`week-overview-${i}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {isCurrentWeek(s) && <div className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0"></div>}
                          <span className="text-sm font-medium text-gray-800">{formatDate(s.week_start)} - {formatDate(s.week_end)}</span>
                        </div>
                        <span className="text-xs text-gray-500">
                          {(s.deacon_names||[]).slice(0,2).join('، ')}{(s.deacon_names||[]).length > 2 && ` +${s.deacon_names.length-2}`}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12" data-testid="no-schedules">
              <CalendarIcon className="w-12 h-12 mx-auto text-amber-300 mb-4" />
              <p className="text-amber-600 mb-4">{t('noSchedule')}</p>
              <Button onClick={() => ensureSchedules(activeService)} className="bg-amber-500 hover:bg-amber-600 text-white">
                {t('refreshSchedules')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="edit-schedule-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-900">{t('edit')} - {editingSchedule && formatDate(editingSchedule.week_start)}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input placeholder={t('searchDeacons')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="border-amber-200" data-testid="edit-search-deacons" />
            <p className="text-sm text-amber-600">{t('selected')}: <strong>{selectedDeaconIds.length}</strong></p>
            <div className="space-y-1.5 max-h-48 overflow-y-auto border border-amber-100 rounded-lg p-2">
              {filteredDeacons.map((d) => (
                <button key={d.id} onClick={() => toggleDeacon(d.id)}
                  className={`w-full flex items-center gap-2 p-2 rounded-md transition-all text-sm ${
                    selectedDeaconIds.includes(d.id) ? 'bg-amber-100 border border-amber-300' : 'hover:bg-gray-50 border border-transparent'
                  }`} data-testid={`edit-deacon-${d.id}`}>
                  <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
                    selectedDeaconIds.includes(d.id) ? 'bg-amber-500 text-white' : 'border border-gray-300'
                  }`}>{selectedDeaconIds.includes(d.id) && <Check className="w-3 h-3" />}</div>
                  <span className={`flex-1 ${selectedDeaconIds.includes(d.id) ? 'font-medium' : ''}`}>{d.name}</span>
                  <span className="text-xs text-gray-400">{d.total_participation || 0}</span>
                </button>
              ))}
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)} data-testid="edit-cancel-btn">{t('cancel')}</Button>
              <Button onClick={handleSaveEdit} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="edit-save-btn">{t('save')}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Register Dialog */}
      <Dialog open={registerDialogOpen} onOpenChange={setRegisterDialogOpen}>
        <DialogContent className="max-w-sm" data-testid="register-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-900 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              {t('registerService')} - {activeService}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Reading Type Selector - single for non-readings groups */}
            {activeService !== 'القراءات' && (groupSubTypes[activeService] || []).length > 1 && (
              <div>
                <label className="block text-sm font-medium text-amber-700 mb-1.5">
                  {language === 'ar' ? 'نوع الخدمة' : 'Diensttyp'}
                </label>
                <select
                  value={registerReadingType}
                  onChange={(e) => setRegisterReadingType(e.target.value)}
                  className="w-full border border-amber-200 rounded-md p-2 text-sm bg-white focus:ring-2 focus:ring-amber-400 focus:outline-none"
                  data-testid="register-reading-type"
                >
                  {(groupSubTypes[activeService] || []).map((st) => (
                    <option key={st} value={st}>{st}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-amber-700 mb-1.5">{t('chooseDate')}</label>
              <Input type="date" value={registerDate} onChange={(e) => setRegisterDate(e.target.value)}
                className="border-amber-200" data-testid="register-date-input" />
            </div>
            <div>
              <p className="text-sm text-amber-600 mb-2">{t('deaconsServing')} ({registerSchedule?.deacon_names?.length || 0})</p>
              <div className="space-y-1.5 max-h-56 overflow-y-auto bg-amber-50 rounded-lg p-2 border border-amber-100">
                {(registerSchedule?.deacon_names || []).map((name, idx) => (
                  <div key={idx} className="flex items-center justify-between gap-2 text-sm" dir="ltr">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-amber-200 flex items-center justify-center text-amber-700 font-bold text-xs flex-shrink-0">{idx+1}</div>
                      <span className="font-medium text-gray-800">{name}</span>
                    </div>
                    <span className="text-xs font-medium text-amber-700 bg-amber-100 border border-amber-200 rounded px-2 py-1">{activeService}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setRegisterDialogOpen(false)} data-testid="register-cancel-btn">{t('cancel')}</Button>
              <Button onClick={handleRegisterService} disabled={registering || !registerDate}
                className="bg-green-600 hover:bg-green-700 text-white" data-testid="register-confirm-btn">
                {registering ? <Loader2 className={`w-4 h-4 animate-spin ${isRTL?'ml-1':'mr-1'}`} /> : <CheckCircle className={`w-4 h-4 ${isRTL?'ml-1':'mr-1'}`} />}
                {registering ? t('registering') : t('registerAndUpdate')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rank Distribution Config Dialog */}
      <Dialog open={rankConfigDialogOpen} onOpenChange={setRankConfigDialogOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>إعدادات التوزيع حسب الرتب - {activeService}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 py-4">
            <p className="text-sm text-gray-600">حدد عدد الشمامسة المطلوب من كل رتبة:</p>
            {editableQuotas.map((q, index) => (
              <div key={index} className="flex items-center gap-2">
                <select
                  value={q.rank}
                  onChange={(e) => {
                    const updated = [...editableQuotas];
                    updated[index] = {...q, rank: e.target.value};
                    setEditableQuotas(updated);
                  }}
                  className="flex-1 text-sm border border-amber-200 rounded px-2 py-2 bg-amber-50"
                  data-testid={`quota-rank-select-${index}`}
                >
                  <option value="">-- اختر الرتبة --</option>
                  {ranks.map(r => (
                    <option key={r.id} value={r.name}>{r.name}</option>
                  ))}
                </select>
                <Input
                  type="number"
                  min="1"
                  max="20"
                  value={q.count}
                  onChange={(e) => {
                    const updated = [...editableQuotas];
                    updated[index] = {...q, count: parseInt(e.target.value) || 1};
                    setEditableQuotas(updated);
                  }}
                  className="w-20 text-center"
                  data-testid={`quota-count-input-${index}`}
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setEditableQuotas(editableQuotas.filter((_, i) => i !== index))}
                  className="border-red-300 text-red-600 hover:bg-red-50 px-2"
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            ))}
            <Button
              variant="outline"
              onClick={() => setEditableQuotas([...editableQuotas, {rank: "", count: 1}])}
              className="border-dashed border-amber-300 text-amber-700"
              data-testid="add-quota-btn"
            >
              + إضافة رتبة
            </Button>
            {editableQuotas.length > 0 && (
              <p className="text-sm text-amber-700 font-medium">
                المجموع: {editableQuotas.reduce((s, q) => s + (q.count || 0), 0)} شماس/أسبوع
              </p>
            )}
            <div className="flex gap-2 justify-end mt-2">
              <Button variant="outline" onClick={() => setRankConfigDialogOpen(false)}>إلغاء</Button>
              <Button onClick={handleSaveRankConfig} className="bg-amber-600 hover:bg-amber-700" data-testid="save-rank-config-btn">حفظ</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WeeklyScheduleComponent;
